import { ArrowRight, Headphones } from "lucide-react";
import { BRAND, MEDIA } from "@/data/content";

export default function Hero() {
  const go = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden flex items-center"
    >
      {/* Soft glowing halo backdrop */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.22] pointer-events-none"
        style={{
          backgroundImage: `url(${MEDIA.hero_bg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: "blur(40px) saturate(120%)",
        }}
      />
      <div
        aria-hidden
        className="absolute -top-32 left-1/2 -translate-x-1/2 w-[760px] h-[760px] tree-halo animate-breath"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black pointer-events-none" />

      <div className="relative z-10 mx-auto max-w-7xl px-6 sm:px-10 lg:px-16 pt-32 pb-24 grid lg:grid-cols-12 gap-12 items-center w-full">
        {/* Left — Copy */}
        <div className="lg:col-span-7 animate-fade-up">
          <span className="chip" data-testid="hero-chip">
            <span className="h-1.5 w-1.5 rounded-full bg-brand-amber" />
            Spiritual Life Coaching · Podcast
          </span>

          <h1 className="mt-8 font-serif text-5xl sm:text-6xl md:text-7xl font-light leading-[1.02] tracking-tight text-white">
            A gentle space to <em className="italic text-rainbow not-italic font-medium">remember</em>
            <br />
            who you've always been.
          </h1>

          <p className="mt-7 max-w-xl text-zinc-400 text-lg leading-relaxed font-sans">
            Tapping Into Life LLC is the home of spiritual life coaching and the
            <span className="text-white"> Tapping Into Life Podcast </span>
            — soulful conversations and 1:1 work that help you discover your
            spiritual identity, heal what life has stirred, and walk into who
            God created you to be.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <button
              data-testid="hero-cta-book"
              onClick={() => go("contact")}
              className="group inline-flex items-center gap-2 px-7 py-4 rounded-full bg-brand-amber text-black font-semibold hover:bg-brand-amberHover transition shadow-[0_0_40px_-10px_rgba(230,126,34,0.6)]"
            >
              Book a Discovery Call
              <ArrowRight size={18} strokeWidth={1.75} className="group-hover:translate-x-1 transition" />
            </button>
            <button
              data-testid="hero-cta-podcast"
              onClick={() => go("podcast")}
              className="inline-flex items-center gap-2 px-7 py-4 rounded-full border border-sky-500/60 text-white bg-sky-600/20 hover:bg-sky-600/35 hover:border-sky-400 transition"
            >
              <Headphones size={18} strokeWidth={1.5} />
              Listen to the Podcast
            </button>
          </div>

          <div className="mt-14 grid grid-cols-3 max-w-md gap-6 border-t border-white/5 pt-7">
            <div>
              <div className="font-serif text-3xl text-white">7+</div>
              <div className="text-xs text-zinc-500 tracking-widest uppercase mt-1">Years guiding</div>
            </div>
            <div>
              <div className="font-serif text-3xl text-white">200+</div>
              <div className="text-xs text-zinc-500 tracking-widest uppercase mt-1">Souls served</div>
            </div>
            <div>
              <div className="font-serif text-3xl text-white">47</div>
              <div className="text-xs text-zinc-500 tracking-widest uppercase mt-1">Episodes</div>
            </div>
          </div>
        </div>

        {/* Right — Logo Spotlight */}
        <div className="lg:col-span-5 relative animate-fade-up" style={{ animationDelay: "180ms" }}>
          <div className="relative aspect-square max-w-[520px] mx-auto">
            <div className="absolute inset-0 tree-halo animate-breath" />
            <img
              data-testid="hero-logo"
              src={BRAND.logo}
              alt="Tapping Into Life LLC — Rainbow Tree of Life"
              className="relative z-10 w-full h-full object-contain drop-shadow-[0_30px_80px_rgba(230,126,34,0.25)]"
            />
            <div className="absolute -bottom-2 left-0 right-0 text-center">
              <span className="font-accent text-3xl sm:text-4xl text-brand-gold">
                "{BRAND.tagline}"
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll cue */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 hidden sm:flex flex-col items-center gap-2 text-zinc-500">
        <span className="text-[10px] tracking-[0.3em] uppercase">Scroll</span>
        <span className="w-px h-10 bg-gradient-to-b from-brand-gold to-transparent" />
      </div>
    </section>
  );
}
